import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { THEME } from "../../theme/ThemeStore";
import { CustomColors } from "../../types/models";

type Props = {
    hover: boolean;
    colors: CustomColors;
};

export const ROBOT_HEIGHT = 240;

const CuteRobotV1 = styled.div<Props>`
    height: ${ROBOT_HEIGHT}px;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: scale(0.6);
`;

const CircleBg = styled.div<Props>`
    background: ${({ colors }) => colors.robotBackground};
    width: 250px;
    height: 250px;
    border-radius: 100%;
    border: 8px solid ${({ colors }) => colors.robotBorder};
    position: relative;

    &:after {
        content: "";
        position: absolute;
        z-index: 2;
        height: 100%;
        width: 100%;
        top: 6px;
        left: 0;
        border-radius: 100%;
        border-bottom: 10px solid ${({ colors }) => colors.robotBorder};
    }
`;

const RobotHead = styled.div<Props>`
    height: 200px;
    width: 200px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: -20px;
    border: 8px solid ${({ colors }) => colors.robotBorder};
    border-radius: 85px/ 60px;
    background: ${({ colors }) => colors.robotBackgroundWhite};
    z-index: 4;
    &:after {
        content: "";
        position: absolute;
        top: -30px;
        height: 30px;
        width: 10px;
        left: 50%;
        transform: translateX(-50%);
        background: ${({ colors }) => colors.robotBorder};
    }
    &:before {
        content: "";
        position: absolute;
        top: -60px;
        left: 50%;
        transform: translateX(-50%);
        height: 35px;
        width: 35px;
        background: ${({ colors }) => colors.robotBackgroundWhite};
        border: 8px solid ${({ colors }) => colors.robotBorder};
        border-radius: 100%;
    }
`;

const RobotFace = styled.div<Props>`
    height: 120px;
    width: 160px;
    background: ${({ colors }) => colors.robotBackground};
    position: absolute;
    top: ${({ hover }) => (hover ? "40px" : "45px")};
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid ${({ colors }) => colors.robotBorder};
    transition: all 200ms;
    border-radius: 45px;
`;

const Eyes = styled.div<Props>`
    height: 20px;
    width: 20px;
    background: ${({ colors }) => colors.robotBorder};
    border-radius: 100%;
    position: absolute;
    transition: all 200ms;
    top: ${({ hover }) => (hover ? "30px" : "40px")};
`;

const LeftEye = styled(Eyes)`
    left: 30px;
`;

const RightEye = styled(Eyes)`
    right: 30px;
`;

const Mouth = styled.div<Props>`
    height: 45px;
    width: 45px;
    border-radius: 100%;
    border: 8px solid transparent;
    background: ${({ hover, colors }) => (hover ? colors.robotBorder : "transparent;")};
    border-bottom-color: ${({ colors }) => colors.robotBorder};
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 50px;
    overflow: hidden;
    clip-path: ${({ hover }) => (hover ? "polygon(0% 30%, 100% 30%, 100% 100%, 0% 100%);" : "none;")};
    &:after {
        content: "";
        position: absolute;
        width: ${({ hover }) => (hover ? "80%;" : "0;")};
        height: ${({ hover }) => (hover ? "80%;" : "0;")};
        bottom: ${({ hover }) => (hover ? "-30%;" : "0;")};
        left: ${({ hover }) => (hover ? "50%;" : "0;")};
        transform: translateX(-50%);
        background: ${({ colors }) => colors.robotBackgroundWhite};
        border-radius: 100%;
    }
`;

const RobotEar = styled.div<Props>`
    position: absolute;
    height: 100px;
    width: 100px;
    border-radius: 100%;
    background: ${({ colors }) => colors.robotBackgroundWhite};
    border: 8px solid ${({ colors }) => colors.robotBorder};
    z-index: 3;
    top: 30px;
`;

const LeftRobotEar = styled(RobotEar)`
    left: -20px;
`;

const RightRobotEar = styled(RobotEar)`
    right: -20px;
`;

const RobotBody = styled.div<Props>`
    height: 50px;
    width: 100px;
    border: 8px solid ${({ colors }) => colors.robotBorder};
    background: ${({ colors }) => colors.robotBackgroundWhite};
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 25px 25px 30px 30px;
`;

export const Robot = observer(() => {
    const [hover, setHover] = React.useState(false);

    return (
        <CuteRobotV1 colors={THEME.customColors} hover={hover}>
            <CircleBg
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                colors={THEME.customColors}
                hover={hover}
            >
                <LeftRobotEar colors={THEME.customColors} hover={hover} />
                <RobotHead colors={THEME.customColors} hover={hover}>
                    <RobotFace colors={THEME.customColors} hover={hover}>
                        <LeftEye colors={THEME.customColors} hover={hover} />
                        <RightEye colors={THEME.customColors} hover={hover} />
                        <Mouth colors={THEME.customColors} hover={hover} />
                    </RobotFace>
                </RobotHead>
                <RightRobotEar colors={THEME.customColors} hover={hover} />
                <RobotBody colors={THEME.customColors} hover={hover} />
            </CircleBg>
        </CuteRobotV1>
    );
});
